import { resetQueryCache, setQueryIdentity } from "./query";
import { PasswordPage } from "./password";
import { ProfilePage } from "./profile";
import { ActivationPage } from "./activation";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { createAuthClient, type AuthClient } from "@pustak-lms/api-client";
import type { SessionIdentity } from "@pustak-lms/contracts";
import { BrandLogo, type BrandingArtwork } from "./brand";
import { AccountMenu } from "./account-menu";
import { AccountSessionContext, formatAccountRole } from "./account-context";
import { formatOrganizationDisplayName } from '@pustak-lms/utils';

export type AuthMode = "platform" | "manage" | "learn" | "preview";
const sharedClient = createAuthClient();
function openWorkspaceFromLogin(mode: AuthMode) {
  const prefix = mode === "platform" ? "" : mode === "manage" ? "/manage" : "/learn";
  if (window.location.pathname === `${prefix}/login`) {
    window.history.replaceState(null, "", mode === "preview" ? "/learn/preview" : prefix || "/");
  }
}
export function canEnter(identity: SessionIdentity, mode: AuthMode): boolean {
  if (mode === "platform") return identity.user.role.split(",").includes("admin");
  if (identity.membership?.status !== "active" || !identity.organization) return false;
  if (mode === "learn") return identity.membership.memberType === "student";
  return ["owner", "faculty"].includes(identity.membership.memberType);
}
export function AuthBoundary({ mode, children, client = sharedClient, branding }: {
  mode: AuthMode;
  children: (identity: SessionIdentity) => ReactNode;
  client?: Pick<AuthClient, "getSession" | "signIn" | "signOut"> & Partial<Pick<AuthClient, "updateProfile">>;
  branding?: BrandingArtwork;
}) {
  const [identity, setIdentity] = useState<SessionIdentity | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const prefix = mode === "platform" ? "" : mode === "manage" ? "/manage" : "/learn";
  useEffect(() => {
    let active = true;
    setLoading(true); setError("");
    client.getSession().then((session) => {
      if (active) {
        if (session && canEnter(session, mode)) openWorkspaceFromLogin(mode);
        setQueryIdentity(session, mode);
        setIdentity(session);
      }
    }).catch(() => { if (active) setError("Unable to restore your session. Please try again."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [client, mode]);
  async function signIn(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setBusy(true); setError("");
    const form = new FormData(event.currentTarget);
    try {
      await client.signIn(String(form.get("email")), String(form.get("password")));
      const session = await client.getSession();
      if (!session) throw new Error("Session unavailable");
      if (canEnter(session, mode)) openWorkspaceFromLogin(mode);
      setQueryIdentity(session, mode);
      setIdentity(session);
    } catch { setError("Unable to sign in with these credentials. Please try again."); }
    finally { setBusy(false); }
  }
  async function signOut() {
    setBusy(true); setError("");
    try {
      await client.signOut(); resetQueryCache(); setIdentity(null);
      window.history.replaceState(null, "", `${prefix}/login`);
    } catch { setError("Unable to sign out. Please try again."); }
    finally { setBusy(false); }
  }
  async function updateProfile(name: string) {
    if (!identity || mode === "preview") throw new Error("Profile editing unavailable");
    const userId = identity.user.id;
    const normalizedName = name.trim();
    await (client.updateProfile ?? sharedClient.updateProfile)(normalizedName);
    setIdentity(current => current?.user.id === userId ? { ...current, user: { ...current.user, name: normalizedName } } : current);
  }
  if (mode !== "platform" && window.location.pathname.endsWith("/activate")) return <ActivationPage surface={mode === "manage" ? "manage" : "learn"} />;
  const passwordSurface = mode === "platform" ? "platform" : mode === "manage" ? "manage" : "learn";
  if (window.location.pathname === `${prefix}/forgot-password`) return <PasswordPage kind="forgot" surface={passwordSurface} />;
  if (window.location.pathname === `${prefix}/reset-password`) return <PasswordPage kind="reset" surface={passwordSurface} />;
  if (loading) return <main className="auth-screen"><p role="status">Restoring your session…</p></main>;
  if (identity) {
    const missingInstitute = (mode === "learn" || mode === "preview") && !identity.organization;
    const workspaceHref = identity.organization && identity.membership?.status === "active"
      ? identity.membership.memberType === "student" ? "/learn" : "/manage"
      : null;
    const accountControl = <AccountMenu user={{ name: identity.user.name, role: identity.membership?.memberType ?? identity.user.role }} />;
    return <AccountSessionContext.Provider value={{
      email: identity.user.email,
      profileHref: mode === "preview" ? "/learn/preview/profile" : `${prefix}/profile`,
      roleLabel: formatAccountRole(mode === "platform" ? identity.user.role : identity.membership?.memberType ?? identity.user.role),
      updateProfile: mode === "preview" ? undefined : updateProfile,
      changePasswordHref: `${mode === "preview" ? "/manage" : prefix}/change-password`,
      busy,
      error,
      signOut
    }}>
      {canEnter(identity, mode) ? children(identity)
        : window.location.pathname === `${prefix}/profile`
          ? <main className="auth-screen"><ProfilePage identity={identity} surface={passwordSurface} footer={accountControl} /></main>
        : window.location.pathname === `${prefix}/change-password`
          ? <PasswordPage kind="change" surface={passwordSurface} footer={accountControl} />
          : <main className="auth-screen"><section className="auth-card">
            <h1>{missingInstitute ? "Open your institute’s learning URL" : "Access denied"}</h1>
            <p>{missingInstitute ? "We couldn’t identify your institute from this address. Open the learning link provided by your institute to continue." : "Your account does not have access to this application."}</p>
            {workspaceHref ? <a href={workspaceHref}>Open your application</a> : null}{accountControl}
          </section></main>}
    </AccountSessionContext.Provider>;
  }
  return <main className="auth-screen"><section className="auth-card">
    <BrandLogo className="auth-brand" src={branding?.logoUrl} alt={branding?.name ? formatOrganizationDisplayName(branding.name) : "Pustak LMS"} />
    {branding?.name ? <p className="auth-organization-name">{formatOrganizationDisplayName(branding.name)}</p> : null}
    <h1>Sign in</h1>
    <p>{mode === "platform" ? "Platform Administration" : mode === "manage" ? "Your organization workspace" : "Your learning workspace"}</p>
    <form onSubmit={(event) => void signIn(event)}>
      <label htmlFor="auth-email">Email</label><input id="auth-email" name="email" type="email" autoComplete="username" required disabled={busy} />
      <label htmlFor="auth-password">Password</label><input id="auth-password" name="password" type="password" autoComplete="current-password" required maxLength={128} disabled={busy} />
      {error ? <p role="alert" className="auth-error">{error}</p> : null}
      <button className="button button-primary" type="submit" disabled={busy}>{busy ? "Signing in…" : "Sign in"}</button>
    </form>
    <a href={`${prefix}/forgot-password`}>Forgot password?</a>
    <p className="auth-note">Accounts are available by invitation.</p>
  </section></main>;
}
